import {
  capitalize,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Paper,
  Typography,
} from "@mui/material";
import { FieldArray, useFormikContext } from "formik";
import { PtoConfig } from "protos/atk/autotractor";
import { PtoSpeed } from "protos/atk/enums";
import { ptoSpeedToLocaleString } from "common/utils/enumTranslations";
import { StepProps } from "./StepProps";
import { TractorViewModel } from "../TractorViewModel";
import { useTranslation } from "react-i18next";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import React, { FC, useState } from "react";

export const PtoStep: FC<StepProps> = ({ disabled }) => {
  const formikProps = useFormikContext<TractorViewModel>();
  const { values, setFieldValue } = formikProps;
  const { t } = useTranslation();

  const [checkedOverrideBox, setCheckedOverrideBox] = useState<boolean>(false);

  const hasCustomInfo =
    Boolean(formikProps.values.frontPto) ||
    Boolean(formikProps.values.rearPto) ||
    checkedOverrideBox;

  const isDisabled = disabled || !hasCustomInfo;
  return (
    <div className="flex flex-col gap-2">
      <Paper className="flex flex-col gap-2 p-4">
        <div className="flex gap-1">
          <InfoOutlinedIcon />
          <Typography>
            {capitalize(t("views.autotractor.equipment.customInfoBlurb"))}
          </Typography>
        </div>
        <FormControlLabel
          label={t("views.autotractor.equipment.tractors.hasCustomPtoInfo")}
          checked={hasCustomInfo}
          disabled={disabled}
          control={<Checkbox />}
          onChange={() => {
            if (checkedOverrideBox) {
              formikProps.setFieldValue("frontPto", undefined);
              formikProps.setFieldValue("rearPto", undefined);
            } else {
              formikProps.setFieldValue("frontPto", values.archetype?.frontPto);
              formikProps.setFieldValue("rearPto", values.archetype?.rearPto);
            }
            setCheckedOverrideBox(!checkedOverrideBox);
          }}
        />
      </Paper>
      <Paper className="flex flex-col gap-2 p-4">
        <Typography className="font-bold">
          {t("models.equipment.pto_short")}
        </Typography>
        {hasCustomInfo ? (
          <div className="flex flex-col">
            <div className="flex flex-col px-2">
              <div className="flex gap-2">
                <FormControlLabel
                  checked={Boolean(values.frontPto)}
                  disabled={isDisabled}
                  onChange={() =>
                    values.frontPto
                      ? setFieldValue("frontPto", undefined)
                      : setFieldValue("frontPto", PtoConfig.fromPartial({}))
                  }
                  control={<Checkbox />}
                  label={capitalize(t("utils.descriptors.front"))}
                />
                {values.frontPto && (
                  <FormControlLabel
                    className="ml-auto"
                    checked={Boolean(values.frontPto.disabled)}
                    disabled={isDisabled}
                    onChange={() =>
                      setFieldValue(
                        "frontPto.disabled",
                        !values.frontPto?.disabled
                      )
                    }
                    control={<Checkbox />}
                    label={capitalize(t("utils.descriptors.disabled"))}
                  />
                )}
              </div>
              {values.frontPto && (
                <div className="p-4 flex flex-col gap-2 bg-opacity-10 bg-black rounded-lg">
                  <Typography>
                    {capitalize(t("utils.descriptors.speed_other"))}
                  </Typography>

                  <FieldArray
                    name="frontPto.availableSpeeds"
                    render={(arrayHelpers) => (
                      <FormGroup row className="justify-evenly">
                        {[
                          PtoSpeed.PTO_SPEED_540,
                          PtoSpeed.PTO_SPEED_540E,
                          PtoSpeed.PTO_SPEED_1000E,
                          PtoSpeed.PTO_SPEED_1000,
                        ].map((s) => (
                          <FormControlLabel
                            disabled={isDisabled}
                            key={`front-speed-${s}`}
                            onChange={() => {
                              const indexofSpeed =
                                values.frontPto?.availableSpeeds.indexOf(s) ??
                                -1;
                              if (indexofSpeed === -1) {
                                arrayHelpers.push(s);
                              } else {
                                arrayHelpers.remove(indexofSpeed);
                              }
                            }}
                            checked={values.frontPto?.availableSpeeds.includes(
                              s
                            )}
                            control={<Checkbox />}
                            label={ptoSpeedToLocaleString(s)}
                          />
                        ))}
                      </FormGroup>
                    )}
                  />
                </div>
              )}
            </div>
            <div className="flex flex-col px-2">
              <div className="flex gap-2">
                <FormControlLabel
                  checked={Boolean(values.rearPto)}
                  disabled={isDisabled}
                  onChange={() =>
                    values.rearPto
                      ? setFieldValue("rearPto", undefined)
                      : setFieldValue("rearPto", PtoConfig.fromPartial({}))
                  }
                  control={<Checkbox />}
                  label={capitalize(t("utils.descriptors.rear"))}
                />
                {values.rearPto && (
                  <FormControlLabel
                    className="ml-auto"
                    checked={Boolean(values.rearPto.disabled)}
                    disabled={isDisabled}
                    onChange={() =>
                      setFieldValue(
                        "rearPto.disabled",
                        !values.rearPto?.disabled
                      )
                    }
                    control={<Checkbox />}
                    label={capitalize(t("utils.descriptors.disabled"))}
                  />
                )}
              </div>
              {values.rearPto && (
                <div className="p-4 flex flex-col gap-2 bg-opacity-10 bg-black rounded-lg">
                  <Typography>
                    {capitalize(t("utils.descriptors.speed_other"))}
                  </Typography>
                  <FieldArray
                    name="rearPto.availableSpeeds"
                    render={(arrayHelpers) => (
                      <FormGroup row className="justify-evenly">
                        {[
                          PtoSpeed.PTO_SPEED_540,
                          PtoSpeed.PTO_SPEED_540E,
                          PtoSpeed.PTO_SPEED_1000E,
                          PtoSpeed.PTO_SPEED_1000,
                        ].map((s) => (
                          <FormControlLabel
                            disabled={isDisabled}
                            key={`front-speed-${s}`}
                            onChange={() => {
                              const indexofSpeed =
                                values.rearPto?.availableSpeeds.indexOf(s) ??
                                -1;
                              if (indexofSpeed === -1) {
                                arrayHelpers.push(s);
                              } else {
                                arrayHelpers.remove(indexofSpeed);
                              }
                            }}
                            checked={values.rearPto?.availableSpeeds.includes(
                              s
                            )}
                            control={<Checkbox />}
                            label={ptoSpeedToLocaleString(s)}
                          />
                        ))}
                      </FormGroup>
                    )}
                  />
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="flex flex-col">
            <div className="px-2">
              <FormControlLabel
                checked={Boolean(values.archetype?.frontPto)}
                disabled={true}
                control={<Checkbox />}
                label={capitalize(t("utils.descriptors.front"))}
              ></FormControlLabel>
              {values.archetype?.frontPto && (
                <div className="p-4 flex flex-col gap-2 bg-opacity-10 bg-black rounded-lg">
                  <Typography>
                    {capitalize(t("utils.descriptors.speed_other"))}
                  </Typography>
                  <FieldArray
                    name="archetype.frontPto.availableSpeeds"
                    render={() => (
                      <FormGroup row className="justify-evenly">
                        {[
                          PtoSpeed.PTO_SPEED_540,
                          PtoSpeed.PTO_SPEED_540E,
                          PtoSpeed.PTO_SPEED_1000E,
                          PtoSpeed.PTO_SPEED_1000,
                        ].map((s) => (
                          <FormControlLabel
                            disabled={true}
                            key={`front-speed-${s}`}
                            checked={values.archetype?.frontPto?.availableSpeeds.includes(
                              s
                            )}
                            control={<Checkbox />}
                            label={ptoSpeedToLocaleString(s)}
                          />
                        ))}
                      </FormGroup>
                    )}
                  />
                </div>
              )}
            </div>
            <div className="px-2">
              <FormControlLabel
                checked={Boolean(values.archetype?.rearPto)}
                disabled={true}
                control={<Checkbox />}
                label={capitalize(t("utils.descriptors.rear"))}
              ></FormControlLabel>
              {values.archetype?.rearPto && (
                <div className="p-4 flex flex-col gap-2 bg-opacity-10 bg-black rounded-lg">
                  <Typography>
                    {capitalize(t("utils.descriptors.speed_other"))}
                  </Typography>
                  <FieldArray
                    name="archetype.rearPto.availableSpeeds"
                    render={() => (
                      <FormGroup row className="justify-evenly">
                        {[
                          PtoSpeed.PTO_SPEED_540,
                          PtoSpeed.PTO_SPEED_540E,
                          PtoSpeed.PTO_SPEED_1000E,
                          PtoSpeed.PTO_SPEED_1000,
                        ].map((s) => (
                          <FormControlLabel
                            disabled={true}
                            key={`front-speed-${s}`}
                            checked={values.archetype?.rearPto?.availableSpeeds.includes(
                              s
                            )}
                            control={<Checkbox />}
                            label={ptoSpeedToLocaleString(s)}
                          />
                        ))}
                      </FormGroup>
                    )}
                  />
                </div>
              )}
            </div>
          </div>
        )}
      </Paper>
    </div>
  );
};
